<template>
    <div class="goals-view">
        <!-- Header -->
        <HeaderComponent />

        <!-- Goals Navigation Bar -->
        <nav class="goals-navigation">
            <div class="container">
                <div class="d-flex justify-content-start align-items-center">
                    <button @click="goBack" class="btn-back">
                        &lt; zurück
                    </button>
                </div>
            </div>
        </nav>

        <!-- Hero Image -->
        <div class="goals-hero">
            <img src="/images/braunZiele.jpg" alt="Bruchköbel" class="hero-image">
        </div>

        <!-- Goals Content -->
        <main class="goals-content">
            <div class="container">
                <div class="goals-main-layout">
                    <!-- Left side: Goals intro text -->
                    <div class="goals-left">
                        <h1 class="goals-title">Gemeinsam weiter -<br>meine Ziele für Bruchköbel:</h1>
                        <p class="goals-subtitle">Mein Versprechen</p>
                        <p class="goals-text">
                            Ich kandidiere erneut, weil ich überzeugt bin: Bruchköbel verdient eine Bürgermeisterin, die
                            zuhört, anpackt und weiterdenkt – für alle Generationen, für alle Stadtteile, ohne
                            Parteibrille, für unsere
                            gemeinsame Zukunft.
                            <span class="hashtag">#GutFürBruchköbel #weitermachen </span>
                        </p>
                    </div>

                    <!-- Right side: Goals grid -->
                    <div class="goals-right">
                        <div class="goals-grid">
                            <div v-for="goal in allGoals" :key="goal.id" class="goal-card" :data-goal-id="goal.id">
                                <div class="goal-header" @click="toggleGoal(goal.id)">
                                    <h3 class="goal-title">{{ goal.title }}</h3>
                                </div>

                                <div v-if="expandedGoals.includes(goal.id)" class="goal-content">
                                    <p v-if="goal.subtitle" class="goal-subtitle">{{ goal.subtitle }}</p>
                                    <div v-if="goal.details" class="goal-details">
                                        <h4>{{ goal.detailsHeading }}</h4>
                                        <ul>
                                            <li v-for="detail in goal.details" :key="detail">{{ detail }}</li>
                                        </ul>
                                    </div>

                                    <!-- Navigation to Projects -->
                                    <div class="goal-navigation">
                                        <button @click="goToProjects(goal.id)" class="nav-to-projects-btn">
                                            » Projekte zu diesem Ziel ansehen
                                        </button>
                                    </div>

                                    <!-- Goal Actions: Like and Close side by side -->
                                    <div class="goal-actions">
                                        <VotingComponent :goal-id="goal.id" @vote="handleVote" />
                                        <button @click="toggleGoal(goal.id)" class="btn-goal-close">
                                            schließen
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
        <!-- Footer -->
        <FooterComponent />
    </div>
</template>

<script>
import HeaderComponent from '@/components/HeaderComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import VotingComponent from '@/components/VotingComponent.vue'

export default {
    name: 'GoalsView',
    components: {
        HeaderComponent,
        FooterComponent,
        VotingComponent
    },
    data() {
        return {
            expandedGoals: [],
            allGoals: [
                {
                    id: 1,
                    title: 'Politik mit Nähe und Persönlichkeit',
                    subtitle: 'Kommunalpolitik lebt nicht von Parteiprogrammen, sondern von Menschen, die zuhören, gestalten und Verantwortung übernehmen. In den vergangenen sechs Jahren haben wir gemeinsam eine verlässliche und nachvollziehbare Politik entwickelt – geprägt von Offenheit, Nahbarkeit und Dialog.',
                    detailsHeading: 'Deshalb setze ich weiter auf:',
                    details: [
                        'Transparente Entscheidungsprozesse ohne Parteibrille',
                        'Live-Übertragungen und regelmäßige Sprechstunden',
                        'Austausch auf Augenhöhe in allen Stadtteilen'
                    ]
                },
                {
                    id: 2,
                    title: 'Finanzielle Spielräume gestalten',
                    subtitle: 'Die städtischen Finanzen sind zum größten Teil von gesetzlichen Verpflichtungen bestimmt. Dabei dürfen wir aber nicht die Aufgaben vernachlässigen, die uns als Stadt lebenswert machen.',
                    detailsHeading: 'Daher stehe ich für:',
                    details: [
                        'Transparente Haushaltsplanung mit Preisschildern',
                        'Investitionen mit Weitblick statt Investitionsstau',
                        'gerechte Verteilung der Steuerlast'
                    ]
                },
                {
                    id: 3,
                    title: 'Mehr Sicherheit, mehr Miteinander',
                    subtitle: 'Ein gepflegtes Stadtbild schafft Lebensqualität – und Regeln sind der Rahmen, in dem wir friedlich und respektvoll zusammenleben können.',
                    detailsHeading: 'Konkret bedeutet das für mich:',
                    details: [
                        'Schöner gestaltete Grünflächen und neue Graffiti-Projekte',
                        'Erweiterte Dienstzeiten unserer Stadtpolizei',
                        'Konsequente Maßnahmen gegen Raser',
                        'Stärkere Kooperation mit der Landespolizei'
                    ]
                },
                {
                    id: 4,
                    title: 'Stadtentwicklung mit Zukunft',
                    subtitle: 'Bruchköbel ist erwacht – und diesen Aufbruch gestalten wir weiter. Mit Herzblut, Ideen und einem klaren Plan.',
                    detailsHeading: 'Was als Nächstes ansteht:',
                    details: [
                        'Neue Kita-Plätze für unsere Kleinsten',
                        'Tagespflege für Demenzerkrankte',
                        'Ein Ärztehaus mit innovativem Konzept in der Innenstadt',
                        'Genuss- und Veranstaltungsort im Alten Rathaus',
                        'Entwicklung des Bürgerhausareals und des Gewerbegebiets',
                        'Bezahlbarer Wohnraum z.B. in Roßdorf – nach dem Umzug der Brückenschule'
                    ]
                },
                {
                    id: 5,
                    title: 'Wirtschaft und Handel - unser Motor',
                    subtitle: 'Mit dem Citymanagement und dem Stadtladen haben wir eine gute Basis für unsere Gewerbetreibenden geschaffen.',
                    detailsHeading: 'Dies ist mein Konzept:',
                    details: [
                        'Innenstadtbelebung voranbringen',
                        'Leerstandmanagement durch Pop-up Konzepte ermöglichen',
                        'Aktivierung und Unterstützung des Gewerbevereins bei innovativen Ideen wie z.B. eine Eisbahn',
                        'Ein offenes Ohr für alle Gewerbetreibende',
                        'Dafür das Citymanagement weiter ermöglichen',
                        'Stadtladen-Konzept an die Bedarfe anpassen',
                    ]
                },
                {
                    id: 6,
                    title: 'Mobilität, die Bruchköbel bewegt',
                    subtitle: 'Mit dem Mobilitätskonzept haben wir die Weichen gestellt. Jetzt bringen wir es auf die Straße.',
                    detailsHeading: 'Ich kümmere mich um:',
                    details: [
                        'Direktverbindungen nach Frankfurt mit Bus & Bahn',
                        'Umsetzung des Innenstadt-Parkraumkonzepts',
                        'Aktive Begleitung der Straßenplanung des Landes'
                    ]
                },
                {
                    id: 7,
                    title: 'Kinder, Jugend & Familie – unsere Zukunft',
                    subtitle: 'Familienfreundlichkeit ist kein Etikett – sie muss erlebbar sein. Mit Angeboten, die alltagsnah helfen und Freude machen.',
                    detailsHeading: 'Dafür setze ich mich ein:',
                    details: [
                        'Weiterentwicklung des Spielplatzkonzepts mit Patenmodellen',
                        'Stärkere Einbindung von Familien in städtische Feste',
                        'Ausbau der Kinderbetreuung durch flexible und vielfältige Konzepte'
                    ]
                },
                {
                    id: 8,
                    title: 'Selbstbestimmt und gut umsorgt im Alter',
                    subtitle: 'Unsere Seniorinnen und Senioren gehören mitten in unsere Stadtgesellschaft – mit aktiver Teilhabe und individueller Unterstützung.',
                    detailsHeading: 'Konkret heißt das:',
                    details: [
                        'Mehr Prävention und Unterstützung zu Hause',
                        'Ausbau der Sozialen Dienste als Partner für betreutes Wohnen',
                        'Starke Rolle des Seniorenbeirats mit eigenen Projekten'
                    ]
                },
                {
                    id: 9,
                    title: 'Kultur leben und erleben',
                    subtitle: 'Kultur ist das Herz einer lebendigen Stadt. Und Bruchköbel hat viel Potenzial, das wir noch stärker nutzen wollen.',
                    detailsHeading: 'Ich plane:',
                    details: [
                        'Neue kulturelle Impulse ins Stadthaus und in die Stadtteile',
                        'Ehrenamtliche Stelle zur Kulturförderung',
                        'Feste Kulturformate für mehr Vielfalt und Begegnung'
                    ]
                },
                {
                    id: 10,
                    title: 'Bürgernahe Verwaltung – digital und persönlich',
                    subtitle: 'Die Verwaltung ist für die Menschen da – nicht umgekehrt.',
                    detailsHeading: 'Darauf können Sie sich verlassen:',
                    details: [
                        'Bürgerfreundliche Öffnungszeiten und kurze Wege',
                        'Weitere Digitalisierung – ohne analoge Angebote zu vernachlässigen',
                        'moderne Führungsstrukturen und schnelle Reaktionszeiten bei Ihren Anliegen'
                    ]
                },
            ]
        }
    },
    mounted() {
        // Check for goal anchor in URL and expand corresponding goal
        this.$nextTick(() => {
            this.handleGoalAnchor()
        })
    },
    methods: {
        goBack() {
            this.$router.push('/')
        },
        goToProjects(goalId) {
            this.$router.push(`/projects#goal-${goalId}`)
        },
        toggleGoal(goalId) {
            const index = this.expandedGoals.indexOf(goalId)
            if (index > -1) {
                // If clicking on an already open accordion, close it
                this.expandedGoals.splice(index, 1)
            } else {
                // If opening a new accordion, close all others first
                this.expandedGoals = [goalId]
            }
        },
        handleVote(voteData) {
            console.log('Vote received:', voteData)
            // Here you can handle the vote data (send to backend, etc.)
        },
        handleGoalAnchor() {
            // Check if there's a goal anchor in the URL
            const hash = window.location.hash
            if (hash && hash.startsWith('#goal-')) {
                const goalId = parseInt(hash.replace('#goal-', ''))
                if (goalId && this.allGoals.find(g => g.id === goalId)) {
                    // Expand the corresponding goal
                    this.expandedGoals = [goalId]

                    // Scroll to the specific goal
                    this.$nextTick(() => {
                        const goalElement = document.querySelector(`[data-goal-id="${goalId}"]`)
                        if (goalElement) {
                            goalElement.scrollIntoView({ behavior: 'smooth' })
                        }
                    })
                }
            }
        }
    }
}
</script>